import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect, useUpdateEffect } from 'react-use';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';

import { checkNever } from '@yandex-int/k-common';
import noop from 'lodash/noop';
import { debounce } from 'lodash';

import { saveToSessionStorage, retrieveFromSessionStorage } from '../../utils/web-storage';
import { SimpleCallback } from '../../typings';
import { useStableCallback } from '../../utils/hooks';
import { getMe } from '../user/selectors';

import { getEditor, getIsLightEditor } from './__code-container/utils';
import { EDITOR_TYPES, ObjectAnswer, ROBOT_TABS, RobotProps } from './robot.types';
import { actions, useRobotRun } from './useRobotRun';
import { useRunCode } from './useRunCode';
import { genSamples, getVisibleSampleIndex } from './utils/sample';
import { convertToObjectAnswer } from './utils/convertToObjectAnswer';
import { getCodeFromUserAnswer } from './utils/getCodeFromUserAnswer';

function useCode(props: RobotProps): [ObjectAnswer | undefined, (answer: ObjectAnswer) => void] {
    const userAnswer = props.answer?.user_answer;
    const me = useSelector(getMe);

    const editor = getEditor(props.problem.markup);
    const answer = useMemo(() => {
        return convertToObjectAnswer(
            userAnswer || '',
            editor.editor === EDITOR_TYPES.LIGHT_PYTHON ? EDITOR_TYPES.LIGHT_PYTHON : EDITOR_TYPES.PYTHON
        );
    }, [editor.editor, userAnswer]);

    /** Ключ для кода в sessionStorage */
    const sessionKey = `robot-${editor.editor}-${me?.id}-${JSON.stringify(useParams())}`;

    const setCode = useStableCallback(
        debounce(
            (newAnswer: ObjectAnswer) => {
                // Это было заменено на debounce(), чтобы сохранять черновиковые (disabled) блоки в blockly,
                // иначе они перестают отображаться при переходах между заданиями (ведь answer.code не обновился)
                // if (newAnswer.answer.code === answer.answer.code) {
                //     return;
                // }

                props.setAnswer?.({ markerAnswer: { user_answer: newAnswer } });
                saveToSessionStorage(sessionKey, JSON.stringify(newAnswer));
            },
            100,
            { leading: true, trailing: false }
        )
    );

    // При маунте проверяем, нет ли сохраненного кода в sessionStorage
    useEffect(() => {
        const sessionRawDraft = retrieveFromSessionStorage(sessionKey);

        if (sessionRawDraft) {
            // во избежание гонки ставим нулевой таймаут
            setTimeout(() => {
                setCode(JSON.parse(sessionRawDraft) as ObjectAnswer);
            }, 0);
        }
    }, [setCode, sessionKey]);

    if (props.showCorrectAnswers) {
        const getCorrectAnswer = () => {
            // здесь дублирование для выведения типов
            switch (editor.editor) {
                case EDITOR_TYPES.PYTHON:
                    return { editor: editor.editor, ...editor.author_solution };
                case EDITOR_TYPES.LIGHT_PYTHON:
                    return { editor: editor.editor, ...editor.author_solution };
                case EDITOR_TYPES.BLOCKLY_PYTHON:
                    return { editor: editor.editor, ...editor.author_solution };
                case EDITOR_TYPES.CODE_LIGHT_V2:
                    return { editor: editor.editor, ...editor.author_solution };
                default:
                    checkNever(editor);
                    throw new Error('invalid editor');
            }
        };
        const solution = {
            version: '1' as const,
            answer: getCorrectAnswer(),
        };
        return [solution, noop];
    }

    return [answer, setCode];
}

function useContextValue(props: RobotProps) {
    const { problem, submitAnswer } = props;

    const [sample, setSample] = useState(() => getVisibleSampleIndex(problem.markup)); // Тест
    const samples = useMemo(() => [...genSamples(problem.markup)], [problem.markup]);
    const [currentTab, setCurrentTab] = useState(props.initialTab || ROBOT_TABS.INFO);
    const [code, setCode] = useCode(props);

    const codeString = useMemo(() => getCodeFromUserAnswer(code), [code]);
    const runCode = useRunCode(codeString, props);

    const [state, dispatch] = useRobotRun(sample, runCode, { problem, submitAnswer });

    // смотрим только на user_answer, сам attempt может меняться
    // вместе со spent time, а мы не хотим на это реагировать
    const attemptAnswer = props.attempt?.markers?.user_answer;
    const isAttemptFinishedWithError = !!props.attempt?.markers?.error;
    const markup = problem?.markup;

    // Сбрасываем воспроизведение
    useLayoutEffect(
        () => dispatch(actions.stop()),
        [
            // при изменении ответа
            codeString,
            // при попытке
            attemptAnswer,
            dispatch,
        ]
    );

    // Сбрасываем результаты воспроизведения после ответа
    useLayoutEffect(() => {
        const editor = getEditor(markup);
        if (editor.editor === EDITOR_TYPES.CODE_LIGHT_V2 || isAttemptFinishedWithError) {
            return;
        }
        dispatch(actions.results({ results: undefined }));
    }, [dispatch, attemptAnswer, markup, isAttemptFinishedWithError]);

    // Воспроизводить light после попытки
    const teacherMode = props.teacherMode;
    const isLightEditor = getIsLightEditor(props.problem);
    useUpdateEffect(() => {
        if (!teacherMode && isLightEditor) {
            // timeout чтобы при перезапуске воспроизведения play пришел после reset из useRobotRun
            setTimeout(() => dispatch(actions.play()), 0);
        }
    }, [dispatch, attemptAnswer]);

    const fieldBgRef = useRef<HTMLDivElement>(null);

    useIsomorphicLayoutEffect(() => {
        if (props.showCorrectAnswers) {
            setCurrentTab(ROBOT_TABS.CODE);
        }
    }, [props.showCorrectAnswers]);

    return {
        ...props,
        samples,
        code,
        setCode,
        codeString,
        sample,
        setSample,
        currentTab,
        setCurrentTab,
        state,
        dispatch,
        fieldBgRef,
    };
}

export type RobotContext = ReturnType<typeof useContextValue>;

const robotContext = createContext({} as RobotContext);

const { Provider } = robotContext;

export function useRobotSelector<T>(selector: (state: RobotContext) => T): T {
    return selector(useContext(robotContext));
}

export const RobotProvider = ({ children, ...props }: RobotProps) => (
    <Provider value={useContextValue(props)}>{children}</Provider>
);

type SwitchContext = {
    tooltipSample: number | null;
    onSampleEnter: (s: number) => void;
    onSampleLeave: SimpleCallback;
};

export const switchContext = createContext({} as SwitchContext);

export const SwitchContextProvider = switchContext.Provider;

export function useEditor() {
    return useRobotSelector((s) => getEditor(s.problem?.markup));
}

export function useIsLightEditor() {
    return useRobotSelector((s) => getIsLightEditor(s.problem));
}
